import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Radar, RefreshCw, ExternalLink, Check, X, Info,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const DEPARTEMENTS = {
  "75": "Paris", "77": "Seine-et-Marne", "78": "Yvelines",
  "91": "Essonne", "92": "Hauts-de-Seine", "93": "Seine-Saint-Denis",
  "94": "Val-de-Marne", "95": "Val-d'Oise",
};

const dateFr = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString("fr-FR");
};

export default function VeillePage() {
  const [avis, setAvis] = useState([]);
  const [compte, setCompte] = useState({});
  const [sources, setSources] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [enCours, setEnCours] = useState(false);

  useEffect(() => { charger(); }, [filtre]);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/veille`,
                                { params: filtre ? { statut: filtre } : {} });
      setAvis(r.data.avis || []);
      setCompte(r.data.compte || {});
    } catch {
      toast.error("Chargement impossible");
    }
  };

  const rafraichir = async () => {
    setEnCours(true);
    try {
      const r = await axios.post(`${API}/veille/rafraichir`);
      const d = r.data || {};
      toast.success(
        d.ajoutes
          ? `${d.ajoutes} nouvel${d.ajoutes > 1 ? "s" : ""} avis`
          : "Aucun nouvel avis");
      charger();
    } catch {
      toast.error("Interrogation du BOAMP impossible");
    } finally {
      setEnCours(false);
    }
  };

  const decider = async (a, statut) => {
    try {
      await axios.put(`${API}/veille/${a.id}`, { statut });
      charger();
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const voirSources = async () => {
    if (sources) return setSources(null);
    try {
      const r = await axios.get(`${API}/veille/sources`);
      setSources(r.data);
    } catch {
      toast.error("Information indisponible");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Radar className="h-6 w-6" /> Veille des appels d'offres
          </h1>
          <p className="text-sm text-muted-foreground">
            Marchés publics d'Île-de-France mentionnant la restauration
            mobile.
          </p>
        </div>
        <Button onClick={rafraichir} disabled={enCours}>
          <RefreshCw className="h-4 w-4 mr-1" />
          {enCours ? "Interrogation…" : "Chercher maintenant"}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap text-sm">
        {[["", "À traiter"], ["retenu", "Retenus"], ["ecarte", "Écartés"]]
          .map(([v, lib]) => (
          <button
            key={v || "tous"}
            onClick={() => setFiltre(v)}
            className={`border rounded-md px-3 py-1 ${
              filtre === v ? "bg-muted font-medium" : "hover:bg-muted/40"}`}
          >
            {lib}
            {v && compte[v] ? ` (${compte[v]})` : ""}
            {!v && compte.nouveau ? ` (${compte.nouveau})` : ""}
          </button>
        ))}
        <button onClick={voirSources}
                className="text-xs underline text-muted-foreground ml-auto">
          <Info className="h-3.5 w-3.5 inline mr-1" />
          Ce que couvre la veille
        </button>
      </div>

      {sources && (
        <Card className="bg-muted/30">
          <CardContent className="pt-5 text-sm space-y-3">
            {sources.automatisees.map((s, i) => (
              <div key={i}>
                <p className="font-medium">{s.nom}</p>
                <p className="text-muted-foreground">{s.detail}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Mots recherchés : {s.mots_cles.join(" · ")}
                </p>
              </div>
            ))}
            <div>
              <p className="font-medium">Ce qui n'est pas couvert</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                {sources.non_automatisables.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {avis.map((a) => (
        <Card key={a.id} className={a.expire ? "opacity-60" : ""}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <CardTitle className="text-base">{a.objet}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {a.acheteur}
                </p>
              </div>
              <div className="flex gap-1 flex-wrap items-start">
                {(a.departements || []).map((d) => (
                  <Badge key={d} variant="outline">
                    {DEPARTEMENTS[d] || d}
                  </Badge>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex gap-4 flex-wrap text-muted-foreground text-xs">
              {a.date_parution && <span>Paru le {dateFr(a.date_parution)}</span>}
              {a.date_limite && (
                <span className={a.expire ? "text-muted-foreground"
                                          : "text-amber-700 font-medium"}>
                  {a.expire ? "Clos le " : "Réponse avant le "}
                  {dateFr(a.date_limite)}
                </span>
              )}
              {a.nature && <span>{a.nature}</span>}
              <span>trouvé via « {a.mot_cle} »</span>
            </div>
            <div className="flex gap-2 justify-end flex-wrap">
              <Button variant="ghost" size="sm" asChild>
                <a href={a.url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" /> Voir l'avis
                </a>
              </Button>
              {a.statut !== "ecarte" && (
                <Button variant="outline" size="sm"
                        onClick={() => decider(a, "ecarte")}>
                  <X className="h-4 w-4 mr-1" /> Écarter
                </Button>
              )}
              {a.statut !== "retenu" && (
                <Button size="sm" onClick={() => decider(a, "retenu")}>
                  <Check className="h-4 w-4 mr-1" /> Retenir
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {!avis.length && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Aucun avis dans cette vue. Lancez une recherche : la première
            interrogation remonte les avis des quatre derniers mois.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
